import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { Holiday, HolidayFormData } from '../../types';

interface HolidayFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: HolidayFormData) => Promise<void>;
  holiday?: Holiday | null;
  loading?: boolean;
}

const initialFormData: HolidayFormData = {
  descricao: '',
  tipo: 'municipal',
  dataInicio: '',
  dataFim: '',
};

const HolidayForm: React.FC<HolidayFormProps> = ({
  open,
  onClose,
  onSubmit,
  holiday,
  loading = false,
}) => {
  const [formData, setFormData] = useState<HolidayFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Carregar dados do feriado para edição
  useEffect(() => {
    if (open) {
      if (holiday) {
        // Modo edição - converter datas para formato datetime-local
        const startDate = new Date(holiday.dataInicio);
        const endDate = new Date(holiday.dataFim);
        
        setFormData({
          descricao: holiday.descricao,
          tipo: holiday.tipo,
          dataInicio: formatDateTimeLocal(startDate),
          dataFim: formatDateTimeLocal(endDate),
        });
      } else {
        // Modo criação - definir data/hora padrão
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0);
        const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59);
        
        setFormData({
          ...initialFormData,
          dataInicio: formatDateTimeLocal(startOfDay),
          dataFim: formatDateTimeLocal(endOfDay),
        });
      }
      setErrors({});
    }
  }, [open, holiday]);

  // Função para formatar data para input datetime-local
  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Validar formulário
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validar descrição
    if (!formData.descricao.trim()) {
      newErrors.descricao = 'Descrição é obrigatória';
    } else if (formData.descricao.length < 2) {
      newErrors.descricao = 'Descrição deve ter pelo menos 2 caracteres';
    } else if (formData.descricao.length > 255) {
      newErrors.descricao = 'Descrição deve ter no máximo 255 caracteres';
    }

    // Validar tipo
    if (!formData.tipo) {
      newErrors.tipo = 'Tipo é obrigatório';
    }

    // Validar datas
    if (!formData.dataInicio) {
      newErrors.dataInicio = 'Data de início é obrigatória';
    }

    if (!formData.dataFim) {
      newErrors.dataFim = 'Data de fim é obrigatória';
    }

    // Validar se data fim é posterior à data início
    if (formData.dataInicio && formData.dataFim) {
      const startDate = new Date(formData.dataInicio);
      const endDate = new Date(formData.dataFim);
      
      if (endDate <= startDate) {
        newErrors.dataFim = 'Data de fim deve ser posterior à data de início';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manipular mudanças nos campos
  const handleChange = (field: keyof HolidayFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: unknown } }
  ) => {
    const value = event.target.value as string;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Submeter formulário
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      // Converter as datas para ISO string
      const submitData: HolidayFormData = {
        ...formData,
        dataInicio: new Date(formData.dataInicio).toISOString(),
        dataFim: new Date(formData.dataFim).toISOString(),
      };
      
      await onSubmit(submitData);
      handleClose();
    } catch (error: any) {
      // Erro será tratado pelo componente pai
    } finally {
      setSubmitting(false);
    }
  };

  // Fechar modal
  const handleClose = () => {
    if (!submitting) {
      setFormData(initialFormData);
      setErrors({});
      onClose();
    }
  };

  // Opções de tipo
  const tipoOptions = [
    { value: 'nacional', label: 'Nacional' },
    { value: 'estadual', label: 'Estadual' },
    { value: 'municipal', label: 'Municipal' },
  ];

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
      }}
    >
      <DialogTitle sx={{ p: 1.5 }}>
        <Typography variant="h6" component="div" sx={{ fontSize: '1.1rem' }}>
          {holiday ? 'Editar Feriado' : 'Novo Feriado'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 1.5 }}>
        <Grid container spacing={1.5}>
          {/* Descrição */}
          <Grid item xs={12}>
            <TextField
              name="descricao"
              label="Descrição *"
              fullWidth
              value={formData.descricao}
              onChange={handleChange('descricao')}
              error={!!errors.descricao}
              helperText={errors.descricao}
              placeholder="Ex: Natal, Independência do Brasil, Aniversário da Cidade"
              disabled={submitting}
              inputProps={{ maxLength: 255 }}
            />
          </Grid>

          {/* Tipo */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.tipo}>
              <InputLabel>Tipo *</InputLabel>
              <Select
                value={formData.tipo}
                label="Tipo *"
                onChange={handleChange('tipo')}
                disabled={submitting}
              >
                {tipoOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.tipo && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.tipo}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Data de Início */}
          <Grid item xs={12} md={6}>
            <TextField
              name="dataInicio"
              label="Data de Início *"
              type="datetime-local"
              fullWidth
              value={formData.dataInicio}
              onChange={handleChange('dataInicio')}
              error={!!errors.dataInicio}
              helperText={errors.dataInicio}
              disabled={submitting}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          {/* Data de Fim */}
          <Grid item xs={12} md={6}>
            <TextField
              name="dataFim"
              label="Data de Fim *"
              type="datetime-local"
              fullWidth
              value={formData.dataFim}
              onChange={handleChange('dataFim')}
              error={!!errors.dataFim}
              helperText={errors.dataFim}
              disabled={submitting}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          {/* Informações adicionais */}
          <Grid item xs={12}>
            <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                <strong>Dica:</strong> As datas incluem horário para maior precisão. 
                Para feriados de dia inteiro, use 00:00 para início e 23:59 para fim.
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 1.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          variant="outlined"
          startIcon={<CancelIcon />}
          size="small"
          sx={{ fontSize: '0.8rem' }}
        >
          Cancelar
        </Button>
        
        <Button
          type="submit"
          disabled={submitting || loading}
          variant="contained"
          startIcon={submitting ? <CircularProgress size={16} /> : <SaveIcon />}
          size="small"
          sx={{ fontSize: '0.8rem' }}
        >
          {submitting ? 'Salvando...' : holiday ? 'Atualizar' : 'Criar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HolidayForm; 